import React from 'react';
import { DAY_LABELS, DEFAULT_OPENING_HOURS } from '../../../lib/shopProfile/helpers';

// A short whitelist of common IANA zones. Full IANA list is 400+
// entries — we surface the ones our target markets actually need and
// keep a free-text override for the long tail. The backend falls back
// to Europe/London silently on garbage values so misconfiguration
// can't break the email flow.
const TIMEZONE_OPTIONS = [
  { value: 'Europe/London',      label: 'London (GMT/BST)' },
  { value: 'Europe/Dublin',      label: 'Dublin (GMT/IST)' },
  { value: 'Europe/Paris',       label: 'Paris (CET/CEST)' },
  { value: 'Europe/Madrid',      label: 'Madrid (CET/CEST)' },
  { value: 'Europe/Berlin',      label: 'Berlin (CET/CEST)' },
  { value: 'Europe/Amsterdam',   label: 'Amsterdam (CET/CEST)' },
  { value: 'Europe/Lisbon',      label: 'Lisbon (WET/WEST)' },
  { value: 'America/New_York',   label: 'New York (ET)' },
  { value: 'America/Chicago',    label: 'Chicago (CT)' },
  { value: 'America/Denver',     label: 'Denver (MT)' },
  { value: 'America/Los_Angeles',label: 'Los Angeles (PT)' },
  { value: 'America/Toronto',    label: 'Toronto (ET)' },
  { value: 'Asia/Dubai',         label: 'Dubai (GST)' },
  { value: 'Asia/Karachi',       label: 'Karachi (PKT)' },
  { value: 'Asia/Kolkata',       label: 'Kolkata (IST)' },
  { value: 'Asia/Singapore',     label: 'Singapore (SGT)' },
  { value: 'Australia/Sydney',   label: 'Sydney (AEDT/AEST)' },
];

// Business hours panel (M4) —
// Shop-level opening hours that show on the public booking page and
// shop-front. Barber hours in /barbers stay per-barber (they drive
// booking availability); shop hours here are what customers see
// advertised.
export const BusinessHoursPanel = ({ form, onChange }) => {
  const tz = form.timezone || 'Europe/London';
  const isKnownTz = TIMEZONE_OPTIONS.some(o => o.value === tz);
  return (
  <div className="panel" style={{ padding: 18, marginBottom: 14 }} data-testid="business-hours-panel">
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
      <div>
        <b style={{ fontSize: 15 }}>Business hours</b>
        <small style={{ display: 'block', color: 'var(--mute)', marginTop: 2, fontSize: 12.5, lineHeight: 1.4 }}>
          Shown on your public booking page &amp; shop-front. Individual barber schedules still drive when customers can book — set those in <a href="/barbers" style={{ color: 'var(--acc)', textDecoration: 'underline' }}>Barbers</a>.
        </small>
      </div>
      <button
        type="button"
        className="btn s"
        onClick={() => onChange({ opening_hours: DEFAULT_OPENING_HOURS })}
        data-testid="hours-reset"
      >Reset to 9-6, Sun closed</button>
    </div>

    {/* Timezone picker — drives what wall-clock time shows in booking
        confirmation / reminder emails. Defaults to Europe/London; the
        backend silently falls back to that if the value is invalid.
        Item 39. */}
    <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }} data-testid="business-hours-timezone-row">
      <label style={{ fontSize: 13, color: 'var(--mute)', flex: '0 0 auto' }}>Timezone</label>
      <select
        className="inp"
        value={isKnownTz ? tz : '__custom__'}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '__custom__') return;   // leave form.timezone as-is
          onChange({ timezone: v });
        }}
        data-testid="business-hours-timezone-select"
        style={{ padding: '6px 8px', fontSize: 13 }}
      >
        {TIMEZONE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        {!isKnownTz && <option value="__custom__">Custom: {tz}</option>}
      </select>
      {!isKnownTz && (
        <input
          className="inp"
          value={tz}
          onChange={(e) => onChange({ timezone: e.target.value })}
          data-testid="business-hours-timezone-custom"
          placeholder="e.g. Europe/Warsaw"
          style={{ flex: 1, minWidth: 160, padding: '6px 8px', fontSize: 13 }}
        />
      )}
    </div>

    <div className="hours-grid" style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
      {(form.opening_hours || []).map((row, idx) => {
        const label = DAY_LABELS[row.weekday];
        const updateRow = (patch) => {
          const next = [...form.opening_hours];
          next[idx] = { ...row, ...patch };
          onChange({ opening_hours: next });
        };
        return (
          <div
            key={row.weekday}
            className="hours-row"
            data-testid={`hours-row-${label.toLowerCase()}`}
            style={{
              display: 'grid',
              gridTemplateColumns: '48px 1fr auto',
              alignItems: 'center',
              gap: 10,
              padding: '8px 10px',
              borderRadius: 8,
              background: row.closed ? 'var(--line2, #f3f4f6)' : 'transparent',
              border: '1px solid var(--line, #e5e7eb)',
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 13 }}>{label}</div>
            {row.closed ? (
              <div style={{ color: 'var(--mute)', fontSize: 13, fontStyle: 'italic' }}>Closed</div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <input
                  className="inp"
                  type="time"
                  value={row.open_time}
                  onChange={(e) => updateRow({ open_time: e.target.value })}
                  data-testid={`hours-${label.toLowerCase()}-open`}
                  style={{ padding: '6px 8px', fontSize: 13, width: 110 }}
                />
                <span style={{ color: 'var(--mute)', fontSize: 12 }}>to</span>
                <input
                  className="inp"
                  type="time"
                  value={row.close_time}
                  onChange={(e) => updateRow({ close_time: e.target.value })}
                  data-testid={`hours-${label.toLowerCase()}-close`}
                  style={{ padding: '6px 8px', fontSize: 13, width: 110 }}
                />
              </div>
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--mute)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!row.closed}
                onChange={(e) => updateRow({ closed: e.target.checked })}
                data-testid={`hours-${label.toLowerCase()}-closed`}
              />
              Closed
            </label>
          </div>
        );
      })}
    </div>
  </div>
  );
};
